import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { signInWithCustomToken } from 'firebase/auth'
import { collection, doc, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import { GlobalState } from '@store'
import { selectCredentials, selectIsLoading } from '@views/loginView/state/loginSlice'
import { getProfile, userProfile } from '@views/profile/state/profileSlice'
import {
  bootstrapTimeTracker,
  logoutStopTimeTracker,
  pauseTimeTracker,
  resumeTimeTracker,
  startTimeTracker,
  stopTimeTracker
} from '../api'
import { getFirebaseClients, isFirebaseConfigured, signOutOfficeTracker } from '../firebase/client'
import { getErrorMessage, normalizeRole, TRACKER_ROLES } from '../utils/timeTrackerUi'
import { TrackerEntry, TrackerPause, TrackerSession, TrackerStartLocationPayload } from '../types'

interface OfficeTimeTrackerContextValue {
  initialized: boolean
  featureEnabled: boolean
  loading: boolean
  normalizedRole: string | null
  session: TrackerSession | null
  weekEntries: TrackerEntry[]
  pausesByEntryId: Record<string, TrackerPause[]>
  error: string | null
  startTimer: (location?: TrackerStartLocationPayload) => Promise<TrackerSession>
  pauseTimer: (pauseType: 'break' | 'almuerzo') => Promise<TrackerSession>
  resumeTimer: () => Promise<TrackerSession>
  stopTimer: () => Promise<TrackerSession>
  logoutAndStop: () => Promise<TrackerSession | null>
  fetchPausesForEntry: (entryId?: string | null) => Promise<TrackerPause[]>
  resetTrackerState: () => Promise<void>
}

const initialState = {
  initialized: false,
  initializedForUserId: null as string | null,
  featureEnabled: false,
  loading: false,
  normalizedRole: null as string | null,
  session: null as TrackerSession | null,
  weekEntries: [] as TrackerEntry[],
  pausesByEntryId: {} as Record<string, TrackerPause[]>,
  error: null as string | null,
  userId: null as string | null
}

const OfficeTimeTrackerContext = createContext<OfficeTimeTrackerContextValue | undefined>(undefined)

const OfficeTimeTrackerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()
  const { cocap, userId } = useSelector(selectCredentials)
  const { isLogged } = useSelector(selectIsLoading)
  const { role } = useSelector(userProfile)
  const profileLoadingStatus = useSelector((state: GlobalState) => state.profile.loadingStatus)
  const [state, setState] = useState(initialState)
  const stateRef = useRef(initialState)
  const unsubscribeRef = useRef<null | (() => void)>(null)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  const cleanupSubscription = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current()
      unsubscribeRef.current = null
    }
  }

  const resetTrackerState = useCallback(async () => {
    cleanupSubscription()

    try {
      await signOutOfficeTracker()
    } catch (error) {
      console.warn('Office tracker Firebase sign out failed', error)
    }

    setState(initialState)
  }, [])

  const fetchWeekEntries = useCallback(async (weekStartDate?: string | null) => {
    const activeUserId = stateRef.current.userId
    if (!activeUserId || !weekStartDate || !isFirebaseConfigured()) {
      setState(previousState => ({
        ...previousState,
        weekEntries: []
      }))
      return []
    }

    const { clientFirestore } = getFirebaseClients()
    const entriesQuery = query(
      collection(clientFirestore, 'time_entries'),
      where('userId', '==', String(activeUserId)),
      where('weekStartDate', '==', weekStartDate),
      orderBy('startedAt', 'asc')
    )

    const snapshot = await getDocs(entriesQuery)
    const entries = snapshot.docs.map(entry => entry.data() as TrackerEntry)
    setState(previousState => ({
      ...previousState,
      weekEntries: entries
    }))
    return entries
  }, [])

  const fetchPausesForEntry = useCallback(async (entryId?: string | null) => {
    if (!entryId || !isFirebaseConfigured()) {
      return []
    }

    const { clientFirestore } = getFirebaseClients()
    const pausesQuery = query(
      collection(clientFirestore, 'time_entries', entryId, 'pauses'),
      orderBy('startedAt', 'asc')
    )
    const snapshot = await getDocs(pausesQuery)
    const pauses = snapshot.docs.map(pause => pause.data() as TrackerPause)

    setState(previousState => ({
      ...previousState,
      pausesByEntryId: {
        ...previousState.pausesByEntryId,
        [entryId]: pauses
      }
    }))

    return pauses
  }, [])

  const bootstrap = useCallback(
    async (trackerUserId: string, trackerRole: string) => {
      const normalizedRole = normalizeRole(trackerRole)
      const featureEnabled = Boolean(normalizedRole && TRACKER_ROLES.has(normalizedRole))
      const currentState = stateRef.current

      if (
        currentState.initialized &&
        currentState.initializedForUserId === trackerUserId &&
        currentState.normalizedRole === normalizedRole
      ) {
        return
      }

      cleanupSubscription()

      if (!featureEnabled) {
        setState({
          ...initialState,
          initialized: true,
          initializedForUserId: trackerUserId,
          normalizedRole,
          userId: trackerUserId
        })
        return
      }

      setState(previousState => ({
        ...previousState,
        loading: true,
        error: null,
        normalizedRole,
        userId: trackerUserId
      }))

      try {
        const response = await bootstrapTimeTracker(cocap)

        if (response.firebaseCustomToken && isFirebaseConfigured()) {
          const { clientAuth, clientFirestore } = getFirebaseClients()
          await signInWithCustomToken(clientAuth, response.firebaseCustomToken)

          const activeSessionRef = doc(clientFirestore, 'time_active_sessions', String(trackerUserId))
          unsubscribeRef.current = onSnapshot(activeSessionRef, snapshot => {
            if (snapshot.exists()) {
              setState(previousState => ({
                ...previousState,
                session: snapshot.data() as TrackerSession
              }))
            }
          })
        }

        setState(previousState => ({
          ...previousState,
          initialized: true,
          initializedForUserId: trackerUserId,
          featureEnabled: response.featureEnabled,
          loading: false,
          normalizedRole: response.normalizedRole,
          session: response.session,
          userId: trackerUserId
        }))

        if (isFirebaseConfigured()) {
          await fetchWeekEntries(response.session?.weekStartDate)

          if (response.session?.currentEntryId) {
            await fetchPausesForEntry(response.session.currentEntryId)
          }
        }
      } catch (error) {
        setState(previousState => ({
          ...previousState,
          initialized: true,
          initializedForUserId: trackerUserId,
          featureEnabled: true,
          loading: false,
          normalizedRole,
          error: getErrorMessage(error, 'No se pudo inicializar el Temporizador oficina'),
          userId: trackerUserId
        }))
      }
    },
    [cocap, fetchPausesForEntry, fetchWeekEntries]
  )

  const applySessionUpdate = useCallback(
    async (sessionPromise: Promise<TrackerSession>, fallbackMessage: string) => {
      setState(previousState => ({
        ...previousState,
        loading: true,
        error: null
      }))

      try {
        const session = await sessionPromise
        setState(previousState => ({
          ...previousState,
          session,
          loading: false
        }))

        if (isFirebaseConfigured()) {
          await fetchWeekEntries(session.weekStartDate)

          if (session.currentEntryId) {
            await fetchPausesForEntry(session.currentEntryId)
          }
        }

        return session
      } catch (error) {
        const message = getErrorMessage(error, fallbackMessage)
        setState(previousState => ({
          ...previousState,
          loading: false,
          error: message
        }))
        throw error
      }
    },
    [fetchPausesForEntry, fetchWeekEntries]
  )

  const startTimer = useCallback(
    (location?: TrackerStartLocationPayload) =>
      applySessionUpdate(startTimeTracker(cocap, location), 'No se pudo iniciar el temporizador'),
    [applySessionUpdate, cocap]
  )
  const pauseTimer = useCallback(
    (pauseType: 'break' | 'almuerzo') =>
      applySessionUpdate(pauseTimeTracker(cocap, pauseType), 'No se pudo pausar el temporizador'),
    [applySessionUpdate, cocap]
  )
  const resumeTimer = useCallback(
    () => applySessionUpdate(resumeTimeTracker(cocap), 'No se pudo reanudar el temporizador'),
    [applySessionUpdate, cocap]
  )
  const stopTimer = useCallback(
    () => applySessionUpdate(stopTimeTracker(cocap), 'No se pudo detener el temporizador'),
    [applySessionUpdate, cocap]
  )

  const logoutAndStop = useCallback(async () => {
    if (!stateRef.current.session || !['running', 'paused'].includes(stateRef.current.session.status)) {
      return null
    }

    return applySessionUpdate(logoutStopTimeTracker(cocap), 'No se pudo cerrar la jornada antes de salir')
  }, [applySessionUpdate, cocap])

  useEffect(() => {
    if (!isLogged) {
      void resetTrackerState()
    }
  }, [isLogged, resetTrackerState])

  useEffect(() => {
    if (isLogged && userId && profileLoadingStatus === 'NOT_STARTED') {
      dispatch(getProfile())
    }
  }, [dispatch, isLogged, profileLoadingStatus, userId])

  useEffect(() => {
    if (isLogged && userId && profileLoadingStatus === 'SUCCESS' && role) {
      void bootstrap(userId, role)
    }
  }, [bootstrap, isLogged, profileLoadingStatus, role, userId])

  useEffect(() => {
    if (state.session?.weekStartDate && isFirebaseConfigured()) {
      void fetchWeekEntries(state.session.weekStartDate)
    }
  }, [fetchWeekEntries, state.session?.weekStartDate])

  return (
    <OfficeTimeTrackerContext.Provider
      value={{
        initialized: state.initialized,
        featureEnabled: state.featureEnabled,
        loading: state.loading,
        normalizedRole: state.normalizedRole,
        session: state.session,
        weekEntries: state.weekEntries,
        pausesByEntryId: state.pausesByEntryId,
        error: state.error,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        logoutAndStop,
        fetchPausesForEntry,
        resetTrackerState
      }}
    >
      {children}
    </OfficeTimeTrackerContext.Provider>
  )
}

const useOfficeTimeTracker = () => {
  const context = useContext(OfficeTimeTrackerContext)

  if (!context) {
    throw new Error('useOfficeTimeTracker must be used within OfficeTimeTrackerProvider')
  }

  return context
}

export { OfficeTimeTrackerProvider, useOfficeTimeTracker }
